import { useEffect, useState } from 'react';
import { cn } from '../../lib/utils';

interface SearchInputProps {
  /** Current value (controlled). */
  readonly value: string;
  /** Called after the user stops typing for `debounceMs`. */
  readonly onChange: (value: string) => void;
  /** Placeholder text inside the input. */
  readonly placeholder?: string;
  /** Accessible label — required (the magnifier icon is not enough). */
  readonly ariaLabel: string;
  /** Debounce window in ms; default 300 (matches the prototype). */
  readonly debounceMs?: number;
  /**
   * Visual variant.
   *
   * - `'default'`: rounded white input with visible border. Used in the
   *   rankings filter bar where it sits on a card surface.
   * - `'panel'`: compact gray-bg (`bg-surface`) with a thin border.
   *   Matches the map's floating-panel `.panel-search` treatment per
   *   `_inbox/luis-fixes/map.html` lines 178-194 (Luis-fix-2: align to
   *   the prototype's compact in-panel search styling).
   */
  readonly variant?: 'default' | 'panel';
}

/** Magnifier icon — inline SVG so we don't pull in an icon library. */
function SearchIcon({ variant }: { variant: 'default' | 'panel' }) {
  // Panel variant: icon sits flush-left next to the text inside the
  // padded gray box. Default variant: icon overlays the input,
  // absolutely positioned so the input padding accommodates it.
  const className =
    variant === 'panel'
      ? 'h-4 w-4 flex-shrink-0 text-text-4'
      : 'pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-4';
  return (
    <svg
      aria-hidden="true"
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

/**
 * Debounced search input. Internal local state mirrors `value` so typing feels
 * instant; `onChange` only fires after `debounceMs` of inactivity. Useful when
 * the consumer triggers an expensive recompute (e.g., filtering 55 metros) on
 * every keystroke.
 *
 * Resets the debounce when the parent changes `value` externally (e.g., a
 * "Clear filters" button calling `onChange('')`).
 *
 * Two visual variants — see `variant` prop docs.
 */
export function SearchInput({
  value,
  onChange,
  placeholder = 'Search…',
  ariaLabel,
  debounceMs = 300,
  variant = 'default',
}: SearchInputProps) {
  const [local, setLocal] = useState<string>(value);

  // Sync internal state when the parent rewrites value (e.g., reset).
  useEffect(() => {
    setLocal(value);
  }, [value]);

  // Debounce: call onChange after the user pauses typing.
  useEffect(() => {
    if (local === value) return;
    const handle = window.setTimeout(() => onChange(local), debounceMs);
    return () => window.clearTimeout(handle);
  }, [local, value, onChange, debounceMs]);

  if (variant === 'panel') {
    // Panel variant: compact gray box with icon flush-left, mirrors
    // `_inbox/luis-fixes/map.html` `.panel-search`. Icon and input
    // share the same horizontal flex row.
    return (
      <label className="flex items-center gap-2 rounded-lg border border-border bg-surface px-[10px] py-[7px]">
        <span className="sr-only">{ariaLabel}</span>
        <SearchIcon variant="panel" />
        <input
          type="search"
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          placeholder={placeholder}
          aria-label={ariaLabel}
          className="w-full border-none bg-transparent text-[13px] text-text-1 outline-none placeholder:text-text-4"
        />
      </label>
    );
  }

  return (
    <label className="relative block">
      <span className="sr-only">{ariaLabel}</span>
      <SearchIcon variant="default" />
      <input
        type="search"
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        className={cn(
          'block w-full rounded-lg border border-border bg-white py-2 pl-9 pr-3 text-sm text-text-1 transition',
          'focus:border-blue focus:outline-none focus:ring-2 focus:ring-blue/20',
        )}
      />
    </label>
  );
}
